import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiFetch } from '../utils/api';
import { useSalesMembers } from '../hooks/useSalesMembers';
import {
  getCurrentYearMonth,
  shouldShowInSalesMonthKanban,
  getStatusCountsForStatsMonth,
  calculatePlannerStats,
} from '../utils/salesProgress';
import {
  PageHeader,
  Button,
  Badge,
  badgeVariantForLabel,
  LoadingState,
  Card,
  KpiCard,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnEmpty,
  SalesKanbanCard,
  SalesMonthFilterSelect,
  SalesProgressFiltersGrid,
  SalesProgressFilterField,
  PersonFilterSelect,
  SalesProgressStatsSection,
  Alert,
} from './ui';

const inputFocus =
  'focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary focus:outline-none';

// 全角数字→半角数字（必要最小限）
const toHalfWidthDigits = (s) => String(s ?? '').replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0));

// 数値文字列に3桁コンマを挿入（表示専用）
const formatNumberWithCommas = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const raw = toHalfWidthDigits(String(val)).replace(/,/g, '');
  if (!/^\d*$/.test(raw)) return String(val);
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// 日付入力（手入力 YYYY/MM/DD + カレンダー選択）
function SmartDateInput({ value, onChange, name, className }) {
  const [displayValue, setDisplayValue] = React.useState('');
  const textRef = React.useRef(null);
  const pickerRef = React.useRef(null);

  React.useEffect(() => {
    if (value) {
      const p = String(value).split('-');
      setDisplayValue(p.length === 3 ? `${p[0]}/${p[1]}/${p[2]}` : String(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleText = (e) => {
    const cleaned = e.target.value.replace(/[^0-9/]/g, '');
    const digits = cleaned.replace(/\//g, '').slice(0, 8);
    let fmt = '';
    if (digits.length <= 4) fmt = digits;
    else if (digits.length <= 6) fmt = `${digits.slice(0, 4)}/${digits.slice(4)}`;
    else fmt = `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6, 8)}`;
    setDisplayValue(fmt);
    if (digits.length === 8) onChange({ target: { name, value: `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` } });
    else if (digits.length === 0) onChange({ target: { name, value: '' } });
  };

  React.useEffect(() => {
    const el = textRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(displayValue.length, displayValue.length);
  }, [displayValue]);

  const openCalendar = () => {
    try { pickerRef.current?.showPicker(); } catch { pickerRef.current?.focus(); }
  };

  return (
    <div className="relative">
      <input
        ref={textRef}
        type="text"
        value={displayValue}
        onChange={handleText}
        className={`${className} pr-10`}
        placeholder="YYYY/MM/DD"
        maxLength={10}
        inputMode="numeric"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={openCalendar}
        tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <i className="fas fa-calendar-alt"></i>
      </button>
      <input
        ref={pickerRef}
        type="date"
        value={value || ''}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const p = e.target.value.split('-');
          if (p.length === 3) {
            setDisplayValue(`${p[0]}/${p[1]}/${p[2]}`);
            onChange({ target: { name, value: e.target.value } });
          }
        }}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden"
      />
    </div>
  );
}

// 時間入力（手入力 HH:MM + ピッカー選択）
function SmartTimeInput({ value, onChange, name, className }) {
  const [displayValue, setDisplayValue] = React.useState('');
  const textRef = React.useRef(null);
  const pickerRef = React.useRef(null);

  React.useEffect(() => { setDisplayValue(value || ''); }, [value]);

  const handleText = (e) => {
    const digits = e.target.value.replace(/[^0-9:]/g, '').replace(/:/g, '').slice(0, 4);
    const fmt = digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
    setDisplayValue(fmt);
    if (digits.length === 4) onChange({ target: { name, value: `${digits.slice(0, 2)}:${digits.slice(2, 4)}` } });
    else if (digits.length === 0) onChange({ target: { name, value: '' } });
  };

  React.useEffect(() => {
    const el = textRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(displayValue.length, displayValue.length);
  }, [displayValue]);

  const openPicker = () => {
    try { pickerRef.current?.showPicker(); } catch { pickerRef.current?.focus(); }
  };

  return (
    <div className="relative">
      <input
        ref={textRef}
        type="text"
        value={displayValue}
        onChange={handleText}
        className={`${className} pr-10`}
        placeholder="HH:MM"
        maxLength={5}
        inputMode="numeric"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <i className="fas fa-clock"></i>
      </button>
      <input
        ref={pickerRef}
        type="time"
        value={value || ''}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          setDisplayValue(e.target.value);
          onChange({ target: { name, value: e.target.value } });
        }}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden"
      />
    </div>
  );
}

// 年月入力（手入力 YYYY/MM + ピッカー選択）
function SmartMonthInput({ value, onChange, name, className }) {
  const [displayValue, setDisplayValue] = React.useState('');
  const textRef = React.useRef(null);
  const pickerRef = React.useRef(null);

  React.useEffect(() => {
    if (value) {
      const p = String(value).split('-');
      setDisplayValue(p.length === 2 ? `${p[0]}/${p[1]}` : String(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleText = (e) => {
    const digits = e.target.value.replace(/[^0-9/]/g, '').replace(/\//g, '').slice(0, 6);
    const fmt = digits.length <= 4 ? digits : `${digits.slice(0, 4)}/${digits.slice(4)}`;
    setDisplayValue(fmt);
    if (digits.length === 6) onChange({ target: { name, value: `${digits.slice(0, 4)}-${digits.slice(4, 6)}` } });
    else if (digits.length === 0) onChange({ target: { name, value: '' } });
  };

  React.useEffect(() => {
    const el = textRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(displayValue.length, displayValue.length);
  }, [displayValue]);

  const openPicker = () => {
    try { pickerRef.current?.showPicker(); } catch { pickerRef.current?.focus(); }
  };

  return (
    <div className="relative">
      <input
        ref={textRef}
        type="text"
        value={displayValue}
        onChange={handleText}
        className={`${className} pr-10`}
        placeholder="YYYY/MM"
        maxLength={7}
        inputMode="numeric"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <i className="fas fa-calendar-alt"></i>
      </button>
      <input
        ref={pickerRef}
        type="month"
        value={value || ''}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const p = e.target.value.split('-');
          if (p.length === 2) {
            setDisplayValue(`${p[0]}/${p[1]}`);
            onChange({ target: { name, value: e.target.value } });
          }
        }}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden"
      />
    </div>
  );
}

// カンバンボードのカラム定義
const KANBAN_COLUMNS = [
  { id: 'scheduling', title: '日程調整中',        icon: 'fa-calendar-plus' },
  { id: 'scheduled',  title: '面談予定',           icon: 'fa-calendar-check' },
  { id: 'completed',  title: '面談済み／回答待ち',  icon: 'fa-handshake' },
  { id: 'won',        title: '成約',               icon: 'fa-trophy' },
  { id: 'declined',   title: '見送り',             icon: 'fa-times-circle' },
];

// ステータスとカラムのマッピング
const STATUS_TO_COLUMN_BP = {
  '日程調整中':         'scheduling',
  '日程調整中（1/2）':  'scheduling',
  '日程調整中（2/2）':  'scheduling',
  '面談予定':           'scheduled',
  '面談予定（1/2）':    'scheduled',
  '面談予定（2/2）':    'scheduled',
  '面談済み／回答待ち':         'completed',
  '面談済み／回答待ち（1/2）':  'completed',
  '面談済み／回答待ち（2/2）':  'completed',
  '成約':    'won',
  '見送り':  'declined',
  // 旧ステータス（後方互換）
  '面談済み':               'completed',
  '回答待ち':               'completed',
  '面談済み（1/2）／回答待ち': 'completed',
  '面談済み（2/2）／回答待ち': 'completed',
};

const COLUMN_TO_STATUS = {
  'scheduling': '日程調整中',
  'scheduled':  '面談予定',
  'completed':  '面談済み／回答待ち',
  'won':        '成約',
  'declined':   '見送り'
};

const formatDateWithTime = (dateValue, timeValue) => {
  if (!dateValue) return '未定';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  const formattedDate = date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return timeValue ? `${formattedDate} ${timeValue}` : formattedDate;
};

const formatStartMonth = (monthValue) => {
  if (!monthValue || monthValue === '未定') return '未定';
  const parts = String(monthValue).split('-');
  if (parts.length !== 2) return String(monthValue);
  return `${parts[0]}年${Number(parts[1])}月`;
};

const getDateTimeForSort = (prospect, fallbackField = 'interview_date') => {
  const dateValue = prospect[fallbackField] || prospect.interview_date || prospect.decision_date || '';
  const timeValue = prospect.interview_time || '00:00';
  if (!dateValue) return null;
  const parsed = new Date(`${dateValue}T${timeValue}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const getRecentChangeTime = (prospect) => {
  const timestamps = (prospect.history || [])
    .map(entry => new Date(entry.timestamp).getTime())
    .filter(time => !Number.isNaN(time));
  return timestamps.length > 0 ? Math.max(...timestamps) : 0;
};

const compareProspectsByColumn = (columnId, a, b) => {
  if (columnId === 'won' || columnId === 'declined') {
    const aTime = getDateTimeForSort(a, 'decision_date') || getRecentChangeTime(a);
    const bTime = getDateTimeForSort(b, 'decision_date') || getRecentChangeTime(b);
    return bTime - aTime;
  }

  const aTime = getDateTimeForSort(a);
  const bTime = getDateTimeForSort(b);
  if (aTime !== null && bTime !== null) return aTime - bTime;
  if (aTime !== null) return -1;
  if (bTime !== null) return 1;
  return getRecentChangeTime(b) - getRecentChangeTime(a);
};

const normalizeBPStatus = (status) => String(status || '');

const parseBPHistoryTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const parsed = new Date(String(timestamp).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const getBPLatestRecordTimestamp = (prospect) => {
  const candidates = [prospect.updated_at, prospect.created_at]
    .map(parseBPHistoryTimestamp)
    .filter(Boolean);
  const historyCandidates = (prospect.history || [])
    .map(entry => parseBPHistoryTimestamp(entry.timestamp))
    .filter(Boolean);
  const allCandidates = [...candidates, ...historyCandidates];
  return allCandidates.length > 0 ? Math.max(...allCandidates) : 0;
};

export default function BPProgress() {
  const { bpMembers, error: membersError } = useSalesMembers();
  const planners = useMemo(
    () => bpMembers.map((member) => member.display_name),
    [bpMembers],
  );

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProspectModal, setShowNewProspectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState('成約');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [editingProspect, setEditingProspect] = useState(null);
  const [selectedPlanner, setSelectedPlanner] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth);
  const [draggedItem, setDraggedItem] = useState(null);
  
  // 自動スクロール用のref
  const scrollContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);

  // 新規登録フォームのバリデーションエラー
  const [newProspectErrors, setNewProspectErrors] = useState({});
  
  // 新規BP見込みフォーム
  const [newProspect, setNewProspect] = useState({
    company_name: '',
    engineer_name: '',
    supplier_name: '',
    interview_date: '',
    interview_time: '',
    decision_date: '',
    start_month: '',
    sales_price: '',
    purchase_price: '',
    main_planner: '',
    support_planners: [],
    status: '',
    notes: ''
  });

  // 初回マウント時にAPIからデータ取得
  useEffect(() => {
    apiFetch('/bp-prospects/')
      .then(res => res.json())
      .then(data => {
        setProspects(Array.isArray(data) ? data : (data.results || []));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const plannerStats = calculatePlannerStats(prospects, planners, selectedMonth);

  // フィルタリング（カンバン: 進行中=常時表示、成約/見送り=営業月内実績）
  const filteredProspects = prospects.filter(prospect => {
    const plannerMatch = selectedPlanner === 'all' ||
      prospect.main_planner === selectedPlanner ||
      prospect.support_planners.includes(selectedPlanner);
    const monthMatch = shouldShowInSalesMonthKanban(prospect, selectedMonth);
    return plannerMatch && monthMatch;
  });

  const statusCounts = getStatusCountsForStatsMonth(prospects, selectedMonth);

  // ステータスサマリー用カラー設定
  const STATUS_SUMMARY_CONFIG = [
    { key: '日程調整中',        icon: 'fa-calendar-plus',  accent: 'neutral' },
    { key: '面談予定',           icon: 'fa-calendar-check', accent: 'info' },
    { key: '面談済み／回答待ち',  icon: 'fa-handshake',      accent: 'warning' },
    { key: '成約',               icon: 'fa-trophy',         accent: 'success' },
    { key: '見送り',             icon: 'fa-times-circle',   accent: 'danger' },
  ];

  const handleDragStart = (e, prospect) => {
    setDraggedItem(prospect);
    e.dataTransfer.effectAllowed = 'move';
  };

  // ドラッグ中の自動スクロール処理
  const handleDrag = (e) => {
    if (!scrollContainerRef.current || !draggedItem) return;
    
    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollThreshold = 100; // スクロール開始までの距離（px）
    const scrollSpeed = 10; // スクロール速度
    
    // マウスがコンテナの左端に近い場合
    if (e.clientX - rect.left < scrollThreshold && e.clientX > 0) {
      if (!autoScrollIntervalRef.current) {
        autoScrollIntervalRef.current = setInterval(() => {
          container.scrollLeft -= scrollSpeed;
        }, 16);
      }
    }
    // マウスがコンテナの右端に近い場合
    else if (rect.right - e.clientX < scrollThreshold && e.clientX > 0) {
      if (!autoScrollIntervalRef.current) {
        autoScrollIntervalRef.current = setInterval(() => {
          container.scrollLeft += scrollSpeed;
        }, 16);
      }
    }
    // 自動スクロールを停止
    else {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (!draggedItem) return;

    const newStatus = COLUMN_TO_STATUS[columnId];
    if (draggedItem.status === newStatus) {
      setDraggedItem(null);
      return;
    }

    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newHistory = [
      ...(draggedItem.history || []),
      { timestamp, user: draggedItem.main_planner, changes: [{ field: 'ステータス', old: draggedItem.status, new: newStatus }] }
    ];

    apiFetch(`/bp-prospects/${draggedItem.id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus, history: newHistory }),
    }).then(res => res.json()).then(updated => {
      setProspects(prev => prev.map(p => p.id === updated.id ? updated : p));
    });

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    // 自動スクロールを停止
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, []);

  // カラムごとにグループ化
  const getProspectsByColumn = (columnId) => {
    return filteredProspects
      .filter(p => STATUS_TO_COLUMN_BP[p.status] === columnId)
      .slice()
      .sort((a, b) => compareProspectsByColumn(columnId, a, b));
  };

  const archiveProspects = prospects
    .filter(p => normalizeBPStatus(p.status) === archiveStatus)
    .slice()
    .sort((a, b) => getBPLatestRecordTimestamp(b) - getBPLatestRecordTimestamp(a));

  // 新規見込み追加
  const handleAddProspect = () => {
    // バリデーション
    const errors = {};
    if (!newProspect.company_name.trim()) errors.company_name = 'クライアント名は必須です';
    if (!newProspect.engineer_name.trim()) errors.engineer_name = 'パートナー名は必須です';
    if (!newProspect.main_planner) errors.main_planner = 'クライアントは必須です';

    if (Object.keys(errors).length > 0) {
      setNewProspectErrors(errors);
      return;
    }
    setNewProspectErrors({});

    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const payload = {
      ...newProspect,
      status: newProspect.status || '日程調整中（1/2）',
      sales_price: newProspect.sales_price === '' ? '' : Number(String(newProspect.sales_price).replace(/,/g, '')),
      purchase_price: newProspect.purchase_price === '' ? '' : Number(String(newProspect.purchase_price).replace(/,/g, '')),
      interview_count: { main: 1, support: newProspect.support_planners.length > 0 ? 0.5 : 0 },
      history: [{ timestamp, user: newProspect.main_planner || '不明', changes: [{ field: '作成', old: '', new: '新規登録' }] }]
    };
    apiFetch('/bp-prospects/', { method: 'POST', body: JSON.stringify(payload) })
      .then(async res => {
        if (!res.ok) {
          let errMsg = `HTTPエラー ${res.status}`;
          try {
            const errData = await res.json();
            errMsg = JSON.stringify(errData);
          } catch {
            try { errMsg = await res.text(); } catch {}
          }
          throw new Error(errMsg);
        }
        return res.json();
      })
      .then(created => {
        setProspects(prev => [created, ...prev]);
        setShowNewProspectModal(false);
        setNewProspect({ company_name: '', engineer_name: '', supplier_name: '', interview_date: '', interview_time: '', decision_date: '', start_month: '', sales_price: '', purchase_price: '', main_planner: '', support_planners: [], status: '', notes: '' });
        setNewProspectErrors({});
      })
      .catch(err => {
        console.error('登録エラー詳細:', err);
        const msg = err?.message ? `登録に失敗しました: ${err.message}` : '登録に失敗しました。入力内容を確認してください。';
        setNewProspectErrors({ api: msg });
      });
  };

  // 編集モーダルを開く
  const handleEdit = (prospect) => {
    setEditingProspect({ ...prospect });
    setShowEditModal(true);
  };

  // 編集を保存
  const handleSaveEdit = () => {
    apiFetch(`/bp-prospects/${editingProspect.id}/`, { method: 'PUT', body: JSON.stringify(editingProspect) })
      .then(res => res.json())
      .then(updated => {
        setProspects(prev => prev.map(p => p.id === updated.id ? updated : p));
        setShowEditModal(false);
        setEditingProspect(null);
      });
  };

  // 削除
  const handleDelete = (id) => {
    if (window.confirm('この見込みを削除してもよろしいですか？')) {
      apiFetch(`/bp-prospects/${id}/`, { method: 'DELETE' })
        .then(() => setProspects(prev => prev.filter(p => p.id !== id)));
    }
  };

  // サポートプランナーのトグル
  const toggleSupportPlanner = (planner) => {
    const current = newProspect.support_planners;
    if (current.includes(planner)) {
      setNewProspect({
        ...newProspect,
        support_planners: current.filter(p => p !== planner)
      });
    } else {
      setNewProspect({
        ...newProspect,
        support_planners: [...current, planner]
      });
    }
  };

  const toggleEditSupportPlanner = (planner) => {
    const current = editingProspect.support_planners;
    if (current.includes(planner)) {
      setEditingProspect({
        ...editingProspect,
        support_planners: current.filter(p => p !== planner)
      });
    } else {
      setEditingProspect({
        ...editingProspect,
        support_planners: [...current, planner]
      });
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6 bg-prodia-background">
      {membersError && (
        <Alert variant="warning" title="営業メンバー情報を取得できませんでした">
          BPプランナーの一覧が表示できない状態です。SalesMember migration 適用後に再度お試しください。
        </Alert>
      )}
      <PageHeader
        title="BP進捗"
        description="カードをドラッグ&ドロップしてステータスを変更 · クリックで詳細表示"
        actions={
          <>
            <Button variant="secondary" onClick={() => { setArchiveStatus('成約'); setShowArchiveModal(true); }}>
              <i className="fas fa-history text-xs"></i>
              履歴
            </Button>
            <Button onClick={() => setShowNewProspectModal(true)}>
              <i className="fas fa-plus text-xs"></i>
              新規案件追加
            </Button>
          </>
        }
        filters={
          <SalesProgressFiltersGrid>
            <SalesProgressFilterField icon="fa-calendar" label="営業月で絞り込み">
              <SalesMonthFilterSelect
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            </SalesProgressFilterField>
            <SalesProgressFilterField icon="fa-user" label="プランナーで絞り込み">
              <PersonFilterSelect
                value={selectedPlanner}
                onChange={(e) => setSelectedPlanner(e.target.value)}
                options={planners}
                allLabel="全プランナー"
              />
            </SalesProgressFilterField>
          </SalesProgressFiltersGrid>
        }
      />

        {/* 実績・分析（常時表示・営業月はヘッダーと共通） */}
        <SalesProgressStatsSection description="プランナー別件数 · ステータス別件数">
          <div>
            <div className="flex gap-4 mb-4 text-xs text-prodia-text-muted">
              <div className="flex items-center gap-1">
                <i className="fas fa-briefcase"></i>
                <span className="font-medium text-prodia-text-secondary">クライアント</span>
                <span>= メインプランナー担当</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="fas fa-handshake"></i>
                <span className="font-medium text-prodia-text-secondary">パートナー</span>
                <span>= パートナー担当</span>
              </div>
            </div>

            <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
              {planners.map(planner => (
                <div
                  key={planner}
                  className="bg-prodia-background border border-prodia-border rounded-xl p-4"
                >
                  <div className="text-center">
                    <div className="text-sm font-semibold text-prodia-text-primary mb-2">{planner}</div>
                    <div className="text-2xl font-semibold text-prodia-text-primary mb-1 tabular-nums">
                      {plannerStats[planner].total.toFixed(1)}件
                    </div>
                    <div className="text-xs space-y-1 text-prodia-text-secondary">
                      <div className="flex items-center justify-center gap-1">
                        <i className="fas fa-briefcase text-[10px]"></i>
                        <span>クライアント: {plannerStats[planner].main.toFixed(1)}件</span>
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <i className="fas fa-handshake text-[10px]"></i>
                        <span>パートナー: {plannerStats[planner].support.toFixed(1)}件</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-prodia-text-primary mb-3">ステータス別件数</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {STATUS_SUMMARY_CONFIG.map(({ key, icon, accent }) => (
                <KpiCard
                  key={key}
                  value={statusCounts[key] ?? 0}
                  label={key}
                  icon={`fas ${icon}`}
                  accent={accent}
                />
              ))}
            </div>
          </div>
        </SalesProgressStatsSection>

        {/* カンバンボード（パイプラインビュー） */}
        <Card padding={false} className="overflow-hidden">
          <KanbanBoard scrollRef={scrollContainerRef}>
            {KANBAN_COLUMNS.map((column) => {
              const columnProspects = getProspectsByColumn(column.id);

              return (
                <KanbanColumn
                  key={column.id}
                  columnId={column.id}
                  title={column.title}
                  count={columnProspects.length}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, column.id)}
                >
                  {columnProspects.map((prospect) => (
                    <SalesKanbanCard
                      key={prospect.id}
                      record={prospect}
                      columnId={column.id}
                      primaryName={prospect.engineer_name || prospect.company_name}
                      secondaryName={
                        [prospect.company_name, prospect.supplier_name].filter(Boolean).join(' · ') ||
                        prospect.company_name
                      }
                      assignee={prospect.main_planner}
                      notes={prospect.notes}
                      isDragging={draggedItem?.id === prospect.id}
                      formatStartMonth={formatStartMonth}
                      onDragStart={(e) => handleDragStart(e, prospect)}
                      onDrag={handleDrag}
                      onDragEnd={handleDragEnd}
                      onOpenDetail={() => {
                        setSelectedProspect(prospect);
                        setShowDetailModal(true);
                      }}
                      onOpenEdit={() => handleEdit(prospect)}
                    />
                  ))}
                  {columnProspects.length === 0 && <KanbanColumnEmpty />}
                </KanbanColumn>
              );
            })}
          </KanbanBoard>
        </Card>

        {/* 詳細モーダル */}
        {showDetailModal && selectedProspect && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
            <div className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-prodia-border" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-prodia-border p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-prodia-text-primary mb-1">{selectedProspect.company_name}</h3>
                    <p className="text-prodia-text-secondary">パートナー: {selectedProspect.engineer_name}{selectedProspect.supplier_name && <span className="ml-2 text-sm">(仕入れ先: {selectedProspect.supplier_name})</span>}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-prodia-text-muted hover:bg-prodia-background rounded-lg p-2 transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-prodia-background rounded-xl p-4 border border-prodia-border">
                    <p className="text-sm text-prodia-text-secondary mb-1">ステータス</p>
                    <Badge variant={badgeVariantForLabel(selectedProspect.status)} showIcon>
                      {selectedProspect.status}
                    </Badge>
                  </div>
                  <div className="bg-prodia-background rounded-xl p-4 border border-prodia-border">
                    <p className="text-sm text-prodia-text-secondary mb-1">クライアント</p>
                    <Badge variant="neutral">{selectedProspect.main_planner}</Badge>
                  </div>
                </div>

                {selectedProspect.support_planners.length > 0 && (
                  <div className="bg-prodia-background rounded-xl p-4 border border-prodia-border">
                    <p className="text-sm text-prodia-text-secondary mb-2">パートナー</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProspect.support_planners.map(sp => (
                        <Badge key={sp} variant="neutral">{sp}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-sm text-green-700 mb-1">面談日時</p>
                    <p className="font-bold text-green-900">
                      {formatDateWithTime(selectedProspect.interview_date, selectedProspect.interview_time)}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <p className="text-sm text-amber-700 mb-1">開始月</p>
                    <p className="font-bold text-amber-900">{formatStartMonth(selectedProspect.start_month)}</p>
                  </div>
                </div>

                {selectedProspect.notes && (
                  <div className="bg-amber-50 rounded-xl p-4">
                    <p className="text-sm text-amber-700 mb-2">備考</p>
                    <p className="text-slate-700">{selectedProspect.notes}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    className="flex-1"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEdit(selectedProspect);
                    }}
                  >
                    <i className="fas fa-edit"></i>
                    編集
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleDelete(selectedProspect.id);
                    }}
                  >
                    <i className="fas fa-trash"></i>
                    削除
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 新規見込み登録モーダル */}
        {showArchiveModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowArchiveModal(false)}>
            <div className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-prodia-border" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-prodia-border p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-prodia-text-primary mb-1">{archiveStatus}履歴</h3>
                    <p className="text-sm text-prodia-text-secondary">画面上では消えた案件も、ここから履歴を確認できます。</p>
                  </div>
                  <button onClick={() => setShowArchiveModal(false)} className="text-prodia-text-muted hover:bg-prodia-background rounded-lg p-2 transition-colors">
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="flex gap-2 mb-4">
                  {['成約', '見送り'].map((status) => (
                    <Button
                      key={status}
                      type="button"
                      size="sm"
                      variant={archiveStatus === status ? 'primary' : 'secondary'}
                      onClick={() => setArchiveStatus(status)}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
                <div className="space-y-3">
                  {archiveProspects.length > 0 ? archiveProspects.map((prospect) => (
                    <button
                      key={prospect.id}
                      type="button"
                      onClick={() => { setShowArchiveModal(false); setSelectedProspect(prospect); setShowDetailModal(true); }}
                      className="w-full text-left bg-slate-50 hover:bg-slate-100 rounded-2xl p-4 border border-slate-200 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-slate-800 truncate">{prospect.company_name}</span>
                            <span className="text-xs text-slate-500">{prospect.engineer_name}</span>
                          </div>
                          <div className="text-xs text-slate-500 space-y-0.5">
                            <p>クライアント: {prospect.main_planner}</p>
                            <p>面談日: {formatDateWithTime(prospect.interview_date, prospect.interview_time)}</p>
                            <p>開始月: {formatStartMonth(prospect.start_month)}</p>
                          </div>
                        </div>
                        <Badge variant={badgeVariantForLabel(archiveStatus)}>{archiveStatus}</Badge>
                      </div>
                    </button>
                  )) : (
                    <div className="text-center py-12 text-slate-400">
                      <i className="fas fa-history text-4xl mb-3 opacity-20"></i>
                      <p className="text-sm">履歴はまだありません</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 新規見込み登録モーダル */}
        {showNewProspectModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowNewProspectModal(false);
                setNewProspect({ company_name: '', engineer_name: '', supplier_name: '', interview_date: '', interview_time: '', decision_date: '', start_month: '', sales_price: '', purchase_price: '', main_planner: '', support_planners: [], status: '', notes: '' });
                setNewProspectErrors({});
              }
            }}
          >
            <div
              className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-2xl my-auto border border-prodia-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-prodia-border p-6">
                <h3 className="text-xl font-semibold text-prodia-text-primary flex items-center gap-2">
                  <i className="fas fa-plus-circle text-prodia-primary"></i>
                  新規BP見込み登録
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                {/* APIエラー表示 */}
                {newProspectErrors.api && (
                  <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-sm text-red-600 flex items-center gap-2">
                    <i className="fas fa-exclamation-circle"></i>
                    {newProspectErrors.api}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      クライアント <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newProspect.company_name}
                      onChange={(e) => {
                        setNewProspect({ ...newProspect, company_name: e.target.value });
                        if (newProspectErrors.company_name) setNewProspectErrors(prev => ({ ...prev, company_name: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                        newProspectErrors.company_name ? 'border-red-400 bg-red-50' : 'border-slate-300'
                      }`}
                      placeholder="例: トヨタ自動車"
                    />
                    {newProspectErrors.company_name && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="fas fa-exclamation-circle"></i>
                        {newProspectErrors.company_name}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      パートナー <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newProspect.engineer_name}
                      onChange={(e) => {
                        setNewProspect({ ...newProspect, engineer_name: e.target.value });
                        if (newProspectErrors.engineer_name) setNewProspectErrors(prev => ({ ...prev, engineer_name: '' }));
                      }}
                      className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                        newProspectErrors.engineer_name ? 'border-red-400 bg-red-50' : 'border-slate-300'
                      }`}
                      placeholder="例: YM"
                    />
                    {newProspectErrors.engineer_name && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="fas fa-exclamation-circle"></i>
                        {newProspectErrors.engineer_name}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">仕入れ先</label>
                  <input
                    type="text"
                    value={newProspect.supplier_name}
                    onChange={(e) => setNewProspect({ ...newProspect, supplier_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    placeholder="例: ○○株式会社"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">面談日</label>
                    <SmartDateInput
                      name="interview_date"
                      value={newProspect.interview_date}
                      onChange={(e) => setNewProspect({ ...newProspect, interview_date: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">面談時間</label>
                    <SmartTimeInput
                      name="interview_time"
                      value={newProspect.interview_time}
                      onChange={(e) => setNewProspect({ ...newProspect, interview_time: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">決定日</label>
                    <SmartDateInput
                      name="decision_date"
                      value={newProspect.decision_date}
                      onChange={(e) => setNewProspect({ ...newProspect, decision_date: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">開始月</label>
                    <SmartMonthInput
                      name="start_month"
                      value={newProspect.start_month}
                      onChange={(e) => setNewProspect({ ...newProspect, start_month: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">売上単価</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">¥</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithCommas(newProspect.sales_price)}
                          onChange={(e) => {
                            const raw = toHalfWidthDigits(e.target.value).replace(/,/g, '');
                            if (raw === '' || /^\d*$/.test(raw)) setNewProspect({ ...newProspect, sales_price: raw });
                          }}
                          className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                          placeholder="例: 600,000"
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">円</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">仕入れ単価</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">¥</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithCommas(newProspect.purchase_price)}
                          onChange={(e) => {
                            const raw = toHalfWidthDigits(e.target.value).replace(/,/g, '');
                            if (raw === '' || /^\d*$/.test(raw)) setNewProspect({ ...newProspect, purchase_price: raw });
                          }}
                          className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                          placeholder="例: 500,000"
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">円</span>
                    </div>
                  </div>
                </div>

                {/* 粗利プレビュー */}
                {(newProspect.sales_price !== '' && newProspect.purchase_price !== '') && (
                  <div className={`rounded-xl px-4 py-3 text-sm font-semibold flex items-center gap-2 ${
                    Number(newProspect.sales_price) - Number(newProspect.purchase_price) >= 0
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    <i className="fas fa-calculator"></i>
                    粗利: {(Number(newProspect.sales_price) - Number(newProspect.purchase_price)).toLocaleString()}円
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    クライアント <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {planners.map(planner => (
                      <button
                        key={planner}
                        type="button"
                        onClick={() => {
                          setNewProspect({ ...newProspect, main_planner: planner });
                          if (newProspectErrors.main_planner) setNewProspectErrors(prev => ({ ...prev, main_planner: '' }));
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          newProspect.main_planner === planner
                            ? 'bg-prodia-primary text-prodia-primary-foreground shadow-sm'
                            : 'bg-prodia-background text-prodia-text-secondary border border-prodia-border hover:bg-prodia-surface'
                        }`}
                      >
                        {planner}
                      </button>
                    ))}
                  </div>
                  {newProspectErrors.main_planner && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle"></i>
                      {newProspectErrors.main_planner}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    パートナー
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {planners.map(planner => (
                      <button
                        key={planner}
                        type="button"
                        onClick={() => toggleSupportPlanner(planner)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          newProspect.support_planners.includes(planner)
                            ? 'bg-prodia-primary text-prodia-primary-foreground shadow-sm'
                            : 'bg-prodia-background text-prodia-text-secondary border border-prodia-border hover:bg-prodia-surface'
                        }`}
                      >
                        {planner}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">ステータス</label>
                  <select
                    value={newProspect.status}
                    onChange={(e) => setNewProspect({ ...newProspect, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                  >
                    <option value="">選択してください</option>
                    <option value="日程調整中">日程調整中</option>
                    <option value="日程調整中（1/2）">日程調整中（1/2）</option>
                    <option value="日程調整中（2/2）">日程調整中（2/2）</option>
                    <option value="面談予定">面談予定</option>
                    <option value="面談予定（1/2）">面談予定（1/2）</option>
                    <option value="面談予定（2/2）">面談予定（2/2）</option>
                    <option value="面談済み／回答待ち">面談済み／回答待ち</option>
                    <option value="面談済み／回答待ち（1/2）">面談済み／回答待ち（1/2）</option>
                    <option value="面談済み／回答待ち（2/2）">面談済み／回答待ち（2/2）</option>
                    <option value="成約">成約</option>
                    <option value="見送り">見送り</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">備考</label>
                  <textarea
                    value={newProspect.notes}
                    onChange={(e) => setNewProspect({ ...newProspect, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    rows="3"
                    placeholder="備考を入力..."
                  ></textarea>
                </div>
              </div>

              <div className="flex gap-3 p-6 bg-prodia-background border-t border-prodia-border rounded-b-xl">
                <Button onClick={handleAddProspect} className="flex-1">
                  <i className="fas fa-check"></i>
                  登録
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowNewProspectModal(false);
                    setNewProspectErrors({});
                  }}
                >
                  <i className="fas fa-times"></i>
                  キャンセル
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 編集モーダル */}
        {showEditModal && editingProspect && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEditModal(false);
                setEditingProspect(null);
              }
            }}
          >
            <div
              className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto border border-prodia-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-prodia-surface border-b border-prodia-border p-6">
                <h3 className="text-xl font-semibold text-prodia-text-primary flex items-center gap-2">
                  <i className="fas fa-edit text-prodia-primary"></i>
                  BP見込み編集
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      クライアント <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingProspect.company_name}
                      onChange={(e) => setEditingProspect({ ...editingProspect, company_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      パートナー <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingProspect.engineer_name}
                      onChange={(e) => setEditingProspect({ ...editingProspect, engineer_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">仕入れ先</label>
                  <input
                    type="text"
                    value={editingProspect.supplier_name || ''}
                    onChange={(e) => setEditingProspect({ ...editingProspect, supplier_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    placeholder="例: ○○株式会社"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">面談日</label>
                    <SmartDateInput
                      name="interview_date"
                      value={editingProspect.interview_date}
                      onChange={(e) => setEditingProspect({ ...editingProspect, interview_date: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">面談時間</label>
                    <SmartTimeInput
                      name="interview_time"
                      value={editingProspect.interview_time}
                      onChange={(e) => setEditingProspect({ ...editingProspect, interview_time: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">決定日</label>
                    <SmartDateInput
                      name="decision_date"
                      value={editingProspect.decision_date || ''}
                      onChange={(e) => setEditingProspect({ ...editingProspect, decision_date: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">開始月</label>
                    <SmartMonthInput
                      name="start_month"
                      value={editingProspect.start_month || ''}
                      onChange={(e) => setEditingProspect({ ...editingProspect, start_month: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">売上単価</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">¥</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithCommas(editingProspect.sales_price ?? '')}
                          onChange={(e) => {
                            const raw = toHalfWidthDigits(e.target.value).replace(/,/g, '');
                            if (raw === '' || /^\d*$/.test(raw)) setEditingProspect({ ...editingProspect, sales_price: raw === '' ? '' : raw });
                          }}
                          className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                          placeholder="例: 600,000"
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">円</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">仕入れ単価</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">¥</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithCommas(editingProspect.purchase_price ?? '')}
                          onChange={(e) => {
                            const raw = toHalfWidthDigits(e.target.value).replace(/,/g, '');
                            if (raw === '' || /^\d*$/.test(raw)) setEditingProspect({ ...editingProspect, purchase_price: raw === '' ? '' : raw });
                          }}
                          className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                          placeholder="例: 500,000"
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">円</span>
                    </div>
                  </div>
                </div>

                {/* 粗利プレビュー */}
                {(editingProspect.sales_price !== '' && editingProspect.sales_price != null &&
                  editingProspect.purchase_price !== '' && editingProspect.purchase_price != null) && (
                  <div className={`rounded-xl px-4 py-3 text-sm font-semibold flex items-center gap-2 ${
                    Number(editingProspect.sales_price) - Number(editingProspect.purchase_price) >= 0
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    <i className="fas fa-calculator"></i>
                    粗利: {(Number(editingProspect.sales_price) - Number(editingProspect.purchase_price)).toLocaleString()}円
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    クライアント <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {planners.map(planner => (
                      <button
                        key={planner}
                        type="button"
                        onClick={() => setEditingProspect({ ...editingProspect, main_planner: planner })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          editingProspect.main_planner === planner
                            ? 'bg-prodia-primary text-prodia-primary-foreground shadow-sm'
                            : 'bg-prodia-background text-prodia-text-secondary border border-prodia-border hover:bg-prodia-surface'
                        }`}
                      >
                        {planner}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    パートナー
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {planners.map(planner => (
                      <button
                        key={planner}
                        type="button"
                        onClick={() => toggleEditSupportPlanner(planner)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          editingProspect.support_planners.includes(planner)
                            ? 'bg-prodia-primary text-prodia-primary-foreground shadow-sm'
                            : 'bg-prodia-background text-prodia-text-secondary border border-prodia-border hover:bg-prodia-surface'
                        }`}
                      >
                        {planner}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">ステータス</label>
                  <select
                    value={editingProspect.status}
                    onChange={(e) => setEditingProspect({ ...editingProspect, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                  >
                    <option value="日程調整中">日程調整中</option>
                    <option value="日程調整中（1/2）">日程調整中（1/2）</option>
                    <option value="日程調整中（2/2）">日程調整中（2/2）</option>
                    <option value="面談予定">面談予定</option>
                    <option value="面談予定（1/2）">面談予定（1/2）</option>
                    <option value="面談予定（2/2）">面談予定（2/2）</option>
                    <option value="面談済み／回答待ち">面談済み／回答待ち</option>
                    <option value="面談済み／回答待ち（1/2）">面談済み／回答待ち（1/2）</option>
                    <option value="面談済み／回答待ち（2/2）">面談済み／回答待ち（2/2）</option>
                    <option value="成約">成約</option>
                    <option value="見送り">見送り</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">備考</label>
                  <textarea
                    value={editingProspect.notes}
                    onChange={(e) => setEditingProspect({ ...editingProspect, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    rows="3"
                  ></textarea>
                </div>
              </div>

              <div className="flex gap-3 p-6 bg-prodia-background border-t border-prodia-border rounded-b-xl">
                <Button onClick={handleSaveEdit} className="flex-1">
                  <i className="fas fa-save"></i>
                  保存
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingProspect(null);
                  }}
                >
                  <i className="fas fa-times"></i>
                  キャンセル
                </Button>
              </div>
            </div>
          </div>
        )}

    </div>
  );
}
